import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// The chrome every onboarding screen repeats: the full-height background, the container
// and the Platanus wordmark. The two variants are the only thing that differed between
// the four Next twins' markup (PORTING.md G5) — the ph26 editions (26-co, 26-mx) put the
// wordmark in a footer and drop `font-title` from the headings, the legacy 25 and 26-ar
// flows pin it to the top-right corner and keep it.
//
// The ph26 wrapper also reproduces the `onboarding/layout.tsx` those two editions ship.

const WORDMARK_STYLE = {
  backgroundColor: 'hsl(var(--primary))',
  maskImage: 'url(/assets/logos/platanus.svg)',
  maskSize: 'contain',
  maskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskImage: 'url(/assets/logos/platanus.svg)',
  WebkitMaskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
} as const;

/** `font-title` on a heading — the legacy chrome has it, the ph26 chrome does not. */
export function titleFont(ph26: boolean): string {
  return ph26 ? '' : 'font-title';
}

export function OnboardingShell({
  ph26,
  width,
  children,
}: {
  ph26: boolean;
  width: 'max-w-2xl' | 'max-w-4xl';
  children: ReactNode;
}) {
  const page = (
    <div className="min-h-screen bg-background">
      <div
        className={
          ph26
            ? 'container mx-auto flex flex-col px-4 py-12 md:py-16'
            : 'container relative mx-auto px-4 py-12 md:py-16'
        }
      >
        <div
          className={
            ph26
              ? 'order-last mt-16 flex justify-center'
              : 'absolute top-4 right-4 z-20 sm:top-6 sm:right-6 md:top-8 md:right-8'
          }
        >
          <div
            className="aspect-[576/112] h-7 w-auto sm:h-9 md:h-10"
            style={WORDMARK_STYLE}
          />
        </div>

        <div className={cn('mx-auto w-full space-y-8', width)}>{children}</div>
      </div>
    </div>
  );

  if (!ph26) return page;

  return (
    <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
      {page}
    </div>
  );
}
